import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { CheckCircle2, AlertTriangle, CalendarDays, Clock, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${API_BASE_URL}/api`;

const DONE_STATUS = {
  project: { value: 'done', label: 'Done', color: '#00C875' },
  marketing: { value: 'published', label: 'Published', color: '#9B30FF' },
  sales: { value: 'won', label: 'Won', color: '#00C875' },
};

const BOARD_TYPE_BADGE = {
  project: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  marketing: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  sales: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

const TaskRow = ({ task, onComplete, completing }) => {
  const assignee = task.assignees?.[0] || task.person || {};
  const badgeClass = BOARD_TYPE_BADGE[task.board_type] || BOARD_TYPE_BADGE.project;

  return (
    <div className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group min-h-[44px]">
      <button
        onClick={() => onComplete(task)}
        disabled={completing}
        className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 hover:border-green-500 dark:hover:border-green-400 flex items-center justify-center transition-colors"
        title="Marquer comme terminé"
        aria-label="Marquer comme terminé"
      >
        {completing
          ? <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
          : <span className="w-2 h-2 rounded-full bg-transparent group-hover:bg-green-500 transition-colors" />
        }
      </button>

      <span className="flex-1 text-sm text-gray-800 dark:text-gray-200 truncate min-w-0">
        {task.task}
      </span>

      <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${badgeClass}`}>
        {task.board_name}
      </span>

      {assignee.name && (
        <div
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
          style={{ backgroundColor: assignee.color || '#1C3E53' }}
          title={assignee.name}
        >
          {assignee.avatar || assignee.name.charAt(0).toUpperCase()}
        </div>
      )}

      {task.status && (
        <span
          className="flex-shrink-0 text-xs px-2 py-0.5 rounded font-medium text-white"
          style={{ backgroundColor: task.status.color || '#CCCCCC' }}
        >
          {task.status.label}
        </span>
      )}
    </div>
  );
};

const TodayFeed = ({ overdue = [], today = [], thisWeek = [], loading, onRefresh }) => {
  const [completing, setCompleting] = useState({});

  const handleComplete = async (task) => {
    const taskId = task._id;
    setCompleting(prev => ({ ...prev, [taskId]: true }));
    const doneStatus = DONE_STATUS[task.board_type] || DONE_STATUS.project;
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: doneStatus }, { withCredentials: true });
      toast({ title: `"${task.task}" marqué comme terminé` });
      onRefresh();
    } catch {
      toast({ title: 'Erreur lors de la mise à jour', variant: 'destructive' });
    } finally {
      setCompleting(prev => ({ ...prev, [taskId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-11 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  const isEmpty = overdue.length === 0 && today.length === 0 && thisWeek.length === 0;

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
        <CheckCircle2 className="w-12 h-12 mb-3 text-green-400" />
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Tout est à jour !</p>
        <p className="text-xs mt-1">Aucune tâche urgente pour le moment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {overdue.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1 pb-2 border-b border-red-100 dark:border-red-900/40">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-sm font-semibold text-red-600 dark:text-red-400">En retard</span>
            <span className="ml-auto text-xs font-bold text-red-500 bg-red-50 dark:bg-red-900/30 px-2 py-0.5 rounded-full">
              {overdue.length}
            </span>
          </div>
          <div>
            {overdue.map(task => (
              <TaskRow
                key={task._id}
                task={task}
                onComplete={handleComplete}
                completing={!!completing[task._id]}
              />
            ))}
          </div>
        </div>
      )}

      {today.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1 pb-2 border-b border-orange-100 dark:border-orange-900/40">
            <CalendarDays className="w-4 h-4 text-[#FE8309] flex-shrink-0" />
            <span className="text-sm font-semibold text-[#FE8309]">Aujourd'hui</span>
            <span className="ml-auto text-xs font-bold text-[#FE8309] bg-orange-50 dark:bg-orange-900/30 px-2 py-0.5 rounded-full">
              {today.length}
            </span>
          </div>
          <div>
            {today.map(task => (
              <TaskRow
                key={task._id}
                task={task}
                onComplete={handleComplete}
                completing={!!completing[task._id]}
              />
            ))}
          </div>
        </div>
      )}

      {thisWeek.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1 pb-2 border-b border-blue-100 dark:border-blue-900/40">
            <Clock className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
            <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">Cette semaine</span>
            <span className="ml-auto text-xs font-bold text-blue-500 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">
              {thisWeek.length}
            </span>
          </div>
          <div>
            {thisWeek.map(task => (
              <TaskRow
                key={task._id}
                task={task}
                onComplete={handleComplete}
                completing={!!completing[task._id]}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TodayFeed;
